import { Component, inject, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatterAdjustmentPropertyTax } from '@models/matter';
import { Store } from '@ngxs/store';
import { SelectSnapshot, ViewSelectSnapshot } from '@ngxs-labs/select-snapshot';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { getDayOfYear, getDaysInYear, getYear, parseISO } from 'date-fns';
import { cloneDeep } from 'lodash';
import { ICON } from '@core/util/icon';
import { calculatePropertyTax } from '../../../../shared/services/adjustments';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AdjustmentsSelector } from '@state/matter/adjustments/adjustments.selectors';
import { AdjustmentsDispatch } from '@state/matter/adjustments';
import { createId } from '@core/util/file';
import { AdjustmentDefaultDetailsSelectors } from '@state/global/adjustment-details';
import { AdjustmentData } from '../adjustments.types';
import { toSignal } from '@angular/core/rxjs-interop';
import { take } from 'rxjs';
@UntilDestroy()
@Component({
	selector: 'app-property-tax-dialog',
	templateUrl: 'property-tax-dialog.component.html',
	styleUrls: ['property-tax-dialog.component.scss'],
	standalone: false,
})
export class PropertyTaxDialogComponent implements OnInit {
	@SelectSnapshot(
		AdjustmentDefaultDetailsSelectors.adjustmentDetailsPropertyTax,
	)
	public adjustmentDetails: boolean;
	@SelectSnapshot(AdjustmentsSelector.defaultMatterAdjustmentPropertyTax)
	defaultMatterAdjustmentPropertyTax: MatterAdjustmentPropertyTax;

	private store = inject(Store);
	adjustmentDate$ = this.store.select(SelectedMatter.adjustmentDate);
	allNonVirtualizedPropertyTaxAdjustments = toSignal(
		this.store
			.select(AdjustmentsSelector.allNonVirtualizedPropertyTaxAdjustments)
			.pipe(take(1)),
	);

	@ViewSelectSnapshot(AdjustmentsSelector.virtualizedPropertyTaxAdjustment)
	virtualizedPropertyTaxAdjustment: MatterAdjustmentPropertyTax;

	@ViewSelectSnapshot(AdjustmentsSelector.propertyTaxAdjustment)
	propertyTaxAdjustmentFilterFn: (id: string) => MatterAdjustmentPropertyTax;

	adjustment: MatterAdjustmentPropertyTax;
	adjustmentDate: string;
	creditAmount: number;
	currentYear: number;
	dayOfYear: number;
	daysInYear: number;
	estimatedIncrease: number;
	estimatedLabel: string;
	readonly ICON = ICON;

	totalLabel: string;
	vendorPaidItems = [
		{
			label: 'Yes',
			value: 'yes',
		},
		{
			label: 'No',
			value: 'no',
		},
	];

	vendorPaidLabel: string;
	vendorShare: number;
	vendorShareLabel: string;

	constructor(
		@Inject(MAT_DIALOG_DATA) public data: AdjustmentData,
		private dialogRef: MatDialogRef<PropertyTaxDialogComponent>,
		private adjustmentDispatch: AdjustmentsDispatch,
	) {}

	ngOnInit(): void {
		if (this.data.adjustmentId) {
			const existingAdjustment = this.propertyTaxAdjustmentFilterFn(
				this.data.adjustmentId,
			);
			if (existingAdjustment) {
				this.adjustment = cloneDeep(existingAdjustment);
			} else {
				this.adjustment = {
					...cloneDeep(this.defaultMatterAdjustmentPropertyTax),
					id: createId(),
					details: this.adjustmentDetails,
				};
			}
		} else {
			this.adjustment = {
				...cloneDeep(this.defaultMatterAdjustmentPropertyTax),
				id: createId(),
				details: this.adjustmentDetails,
			};
		}

		this.creditAmount = 0;
		this.estimatedIncrease = 0;
		this.vendorShare = 0;
		this.adjustmentDate$ // this order is important, `adjustmentDate` should override `closingDate` if it exists
			.pipe(untilDestroyed(this))
			.subscribe((date) => {
				this.adjustmentDate = date;
				this.currentYear = getYear(parseISO(date));
				this.dayOfYear = getDayOfYear(parseISO(date)) - 1;
				this.daysInYear = getDaysInYear(parseISO(date));
				this.vendorShareLabel = `Vendor's share for ${this.dayOfYear} days`;
				this.changed();
			});
	}

	cancelModal(): void {
		this.dialogRef.close();
	}

	changed(resetVendorPaidAmount = false): void {
		switch (this.adjustment.taxBill) {
			case 'final':
				this.totalLabel = `${this.currentYear} Total taxes`;
				this.vendorPaidLabel = 'Vendor will have paid';
				break;
			case 'previous': // this order is important, `adjustmentDate` should override `closingDate` if it exists
				this.estimatedLabel = `Estimated total for ${this.currentYear} taxes`;
				this.totalLabel = `${this.currentYear - 1} Total taxes`;
				this.vendorPaidLabel = `Vendor will have paid (towards ${this.currentYear} taxes)`;
				break;
			case 'interim':
				this.estimatedLabel = `Estimated total for ${this.currentYear} taxes`;
				this.totalLabel = `${this.currentYear} interim bill amount`;
				this.vendorPaidLabel = 'Vendor has paid';
				break;
			case 'estimated':
				this.totalLabel = `Estimated total for ${this.currentYear} taxes`;
				this.vendorPaidLabel = 'Vendor has paid';
				break;
			case 'na':
				this.adjustment.total = this.defaultMatterAdjustmentPropertyTax.total;
				this.adjustment.vendorPaid =
					this.defaultMatterAdjustmentPropertyTax.vendorPaid;
				this.adjustment.vendorPaidAmount =
					this.defaultMatterAdjustmentPropertyTax.vendorPaidAmount;
				break;
		}

		if (resetVendorPaidAmount && this.adjustment.vendorPaid === 'no') {
			this.adjustment.vendorPaidAmount = 0;
		}

		const calculatedPropertyTax = calculatePropertyTax(
			this.adjustment.taxBill,
			`${this.adjustment.total}`,
			`${this.adjustment.increase}`,
			`${this.adjustment.interim}`,
			this.adjustment.vendorPaid,
			`${this.adjustment.vendorPaidAmount}`,
			this.adjustmentDate,
		);

		this.creditAmount = calculatedPropertyTax.creditAmount;
		this.estimatedIncrease = calculatedPropertyTax.estimatedIncrease;
		this.vendorShare = calculatedPropertyTax.vendorShare;
		this.adjustment.vendorPaidAmount = calculatedPropertyTax.vendorPaidAmount;
	}

	saveModal(): void {
		if (!this.data.adjustmentId) {
			// if we're creating a new adjustment for the first time we want to save the old virtualized one to the db
			const adjustments = this.allNonVirtualizedPropertyTaxAdjustments();
			if (adjustments && adjustments.length === 0) {
				this.adjustmentDispatch.Set(this.virtualizedPropertyTaxAdjustment);
			}
		}

		this.adjustmentDispatch.Set(this.adjustment);
		this.dialogRef.close();
	}
}
